import { SGeneralForms, SLocalSelected, SLocalTreeSelected } from 'shj-components'
import React, { useEffect, useMemo, useRef } from 'react'
import { TreeSelect, message, DatePicker, Select } from 'antd'
import { isEmpty } from 'lodash-es'
import dayjs from 'dayjs'
import {
  getBrandPage,
  getGeneralPage,
  getProductCommonPage,
  getProductEnums,
  getProductSkuManagementPage,
  getSpecialCodesList,
} from '@/services/product.ts'
import { useSetState } from 'ahooks'
import DebounceSelect from '@/pages/baseProduct/components/DebounceSearchSelect'
import { httpDictServList } from '@/services'
import { httpOrgList } from '@/services/homeService.ts'

const AddOrEditConfigure = props => {
  const { editRef, current, apiObj } = props
  const ref = useRef()

  const [state, setState] = useSetState({
    searchValueObj: {},
    productList: [],
    searchBrandValueObj: {},
    brandList: [],
    companyList: [],
    specialCodeList: [],
    grossList: [],
  })

  const onFormRef = () => {
    const { onFormRef } = ref.current || {}
    return onFormRef?.()
  }

  useEffect(() => {
    onFormRef()?.resetFields()
    if (!isEmpty(props.record)) {
      const obj = {
        ...props.record,
        startDate: dayjs(props.record.startDate),
        endDate: props.record.endDate && dayjs(props.record.endDate),
      }
      if (props.record.brandName)
        setState({ searchBrandValueObj: { nameKey: props.record.brandName } })
      if (props.record.productId) setState({ searchValueObj: { id: props.record.productId } })
      onFormRef()?.setFieldsValue(obj)
    }
  }, [props.record])

  const getOptions = async () => {
    const companyList: any = await httpOrgList()
    const priceTypeList = await httpDictServList({ parentKey: 'MpmsPriceType' })
    let specialCodeList: any = await getSpecialCodesList({
      data: {},
    })
    let grossList: any = await getProductEnums('com.shj.saas.plus.product.enums.GrossRateType')
    specialCodeList = specialCodeList.map(item => ({ label: item.showName, value: item.code }))
    grossList = grossList.map(item => ({ label: item.description, value: item.key }))
    setState({ companyList, specialCodeList, grossList })
    return Promise.resolve({ companyList, priceTypeList, specialCodeList, grossList })
  }

  const onFinish = () => {
    onFormRef()
      ?.validateFields()
      .then(values => {
        props.setLoading(true)
        const obj = {
          ...values,
          startDate: values.startDate && dayjs(values.startDate).format('YYYY-MM-DD'),
          endDate: values.endDate && dayjs(values.endDate).format('YYYY-MM-DD'),
          sourceType: current.key,
        }
        const apiUrl = values.id ? apiObj.updateById : apiObj.add
        apiUrl({ data: obj })
          .then(res => {
            message.success(res.message || '操作成功')
            props.fn?.()
            props.onClose?.()
          })
          .finally(() => props.setLoading(false))
      })
  }

  if (editRef.current) editRef.current.onFinish = onFinish

  return (
    <>
      <SGeneralForms
        ref={ref}
        layout={'vertical'}
        httpOptionsFn={getOptions}
        onChange={v => console.log(v)}
        initialValues={
          props.record?.id
            ? props.record
            : {
                isStore: true,
                compatibleFlag: true,
                priceType: 'customerPrice',
                grossRateType: state.grossList[0]?.value,
              }
        }
        configs={[
          {
            formItem: [
              { name: 'id', hidden: true },
              {
                spanCol: 24,
                label: '适用产品',
                name: 'productIds',
                required: true,
                isDeps: true,
                deps: () => !props.record?.id,
                rules: [{ required: true, message: '适用产品不能为空' }],
                children: (
                  <DebounceSelect
                    allowClear
                    label={'产品'}
                    isCommon={true}
                    mode={'multiple'}
                    fixedParams={{ prodTypes: ['PRODUCT_PACKAGE'] }}
                    fetchOptions={getProductCommonPage}
                    fieldNames={{ label: 'prodName', value: 'id' }}
                  />
                ),
              },
              {
                spanCol: 24,
                label: '标配SKU',
                name: 'sourceId',
                required: true,
                isDeps: true,
                deps: () => !props.record?.id && current.key === 'STANDARD',
                rules: [{ required: true, message: '标配SKU不能为空' }],
                children: (
                  <DebounceSelect
                    allowClear
                    label={'标配SKU'}
                    nameKey="keyword"
                    query={{ id: props.record.sourceId }}
                    fixedParams={{
                      prodTypes: ['PRODUCT_SINGLE', 'PRODUCT_GROUP', 'PRODUCT_BASIC_ITEM'],
                    }}
                    fetchOptions={getProductSkuManagementPage}
                    fieldNames={{ label: 'showName', value: 'id' }}
                  />
                ),
              },
              {
                spanCol: 24,
                label: '通项',
                name: 'sourceId',
                required: true,
                isDeps: true,
                deps: () => !props.record?.id && current.key === 'GENERAL',
                rules: [{ required: true, message: '通项不能为空' }],
                children: (
                  <DebounceSelect
                    allowClear
                    label={'通项'}
                    nameKey={'itemNameKey'}
                    fetchOptions={getGeneralPage}
                    fieldNames={{ label: 'itemName', value: 'id' }}
                  />
                ),
              },
              {
                spanCol: 24,
                label: '品牌',
                name: 'brandIds',
                isDeps: true,
                deps: () => !props.record?.id && !['STANDARD', 'GENERAL'].includes(current.key),
                children: (
                  <DebounceSelect
                    allowClear
                    label={'品牌'}
                    mode={'multiple'}
                    query={{ id: props.record.brandId }}
                    fetchOptions={getBrandPage}
                  />
                ),
              },
              {
                spanCol: 24,
                label: '是否入库',
                isDeps: true,
                deps: () => current.key === 'promng',
                name: 'isStore',
                type: 'switch',
                comConfig: {
                  checkedChildren: '是',
                  unCheckedChildren: '否',
                },
              },
              {
                name: 'specialCode',
                label: '特项代码',
                spanCol: 24,
                required: !['STANDARD', 'GENERAL'].includes(current.key),
                rules: [
                  {
                    required: !['STANDARD', 'GENERAL'].includes(current.key),
                    message: '特项代码不能为空',
                  },
                ],
                isDeps: true,
                deps: () => !props.record?.id && !['STANDARD', 'GENERAL'].includes(current.key),
                children: (
                  <Select
                    allowClear
                    placeholder="请选择"
                    options={state.specialCodeList}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={e => onFormRef()?.setFieldValue('specialCode', e)}
                  />
                ),
              },
              {
                spanCol: 24,
                label: '是否通配',
                isDeps: true,
                deps: form =>
                  !['STANDARD', 'GENERAL'].includes(current.key) &&
                  form?.getFieldValue('specialCode'),
                name: 'compatibleFlag',
                type: 'switch',
                comConfig: {
                  checkedChildren: '是',
                  unCheckedChildren: '否',
                },
              },
              {
                spanCol: 24,
                label: '毛利类型',
                isDeps: true,
                deps: form => ['gross'].includes(current.key),
                name: 'grossRateType',
                type: 'radio',
                data: 'grossList',
                required: true,
                rules: [{ required: true, message: '毛利类型不能为空' }],
              },
              {
                spanCol: 24,
                label: '',
                isDeps: true,
                deps: form => ['gross'].includes(current.key),
                children: () => {
                  return (
                    <p style={{ marginTop: '-20px', marginLeft: 5, color: '#a9a9a9' }}>
                      用于计算标配/自选/增项等产品子集的客户价
                    </p>
                  )
                },
              },
              {
                spanCol: 24,
                label: current.description + ' (%)',
                name: `${current.key}Value`,
                type: 'inputNumber',
                isDeps: true,
                deps: () => !['STANDARD', 'GENERAL'].includes(current.key),
                required: true,
                rules: [{ required: true, message: current.description + '不能为空' }],
                comConfig: {
                  min: 0,
                  max: 10000,
                },
              },
              {
                spanCol: 24,
                label: '价格类型',
                name: 'priceType',
                required: true,
                rules: [{ required: true, message: '价格类型不能为空' }],
                isDeps: true,
                deps: () => !props.record?.id && ['STANDARD', 'GENERAL'].includes(current.key),
                type: 'select',
                data: 'priceTypeList',
                comConfig: {
                  fieldNames: { label: 'name', value: 'key' },
                },
              },
              {
                spanCol: 24,
                label: '价格（元）',
                name: 'price',
                required: true,
                rules: [{ required: true, message: '价格不能为空' }],
                isDeps: true,
                deps: () => ['STANDARD', 'GENERAL'].includes(current.key),
                type: 'inputNumber',
                comConfig: {
                  min: 0,
                  precision: 2,
                },
              },
              {
                spanCol: 24,
                label: '适用门店',
                name: 'companyIds',
                required: true,
                isDeps: true,
                deps: () => !props.record?.id,
                rules: [{ required: true, message: '适用门店不能为空' }],
                children: (
                  <Select
                    mode={'multiple'}
                    placeholder={'请选择适用门店'}
                    optionFilterProp={'name'}
                    fieldNames={{ label: 'name', value: 'id' }}
                    options={[{ name: '通用', id: '0' }, ...state.companyList]}
                  />
                ),
                // children: <SLocalTreeSelected treeCheckable treeDefaultExpandAll
                //                               showCheckedStrategy={TreeSelect.SHOW_PARENT}
                //                               fieldNames={{label: 'name', value: 'id'}}
                //                               treeData={[{name: '通用', id: '0'}, ...state.companyList]}/>,
              },
              {
                spanCol: 24,
                label: '开始日期',
                name: 'startDate',
                required: true,
                rules: [{ required: true, message: '开始日期不能为空' }],
                type: 'date',
              },
              {
                spanCol: 24,
                label: '结束日期',
                name: 'endDate',
                type: 'date',
                comConfig: {
                  disabledDate: current => {
                    const startDate = onFormRef().getFieldValue('startDate')
                    if (startDate) return current && current < dayjs(startDate).endOf('day')
                  },
                },
              },
            ],
          },
        ]}
      />
    </>
  )
}

export default AddOrEditConfigure
